import axios, { AxiosError, AxiosResponse } from 'axios'
import config from '../config'
import { IHTTPErrorResponse, IHTTPSuccessResponse } from './http.types'
import { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { IAuthState } from '../../screens/AuthProvider/auth.atom'
import authApi from '../../screens/AuthProvider/auth.api'

const http = axios.create({
  baseURL: config.API_URL,
  headers: { 'Accept-Language': 'ru-RU' },
  adapter: require('axios/lib/adapters/xhr'),
})

let interceptorsApplied = false
export const applyInterceptors = (
  authState: MutableRefObject<IAuthState>,
  setAuthState: Dispatch<SetStateAction<IAuthState>>
) => {
  if (interceptorsApplied) return

  interceptorsApplied = true
  let isRefreshing = false

  let refreshRequest = Promise.resolve({
    accessToken: authState.current.accessToken,
    accessExpires: authState.current.accessExpires,
  })

  const ensureAuthorization = (): Promise<Pick<IAuthState, 'accessToken' | 'accessExpires'>> => {
    const shouldRefresh = !authState || Date.now() + 600000 > authState.current.accessExpires

    return shouldRefresh ? refreshToken(authState) : Promise.resolve(authState.current)
  }

  const refreshToken = async (
    authState: MutableRefObject<IAuthState>
  ): Promise<Pick<IAuthState, 'accessToken' | 'accessExpires'>> => {
    if (isRefreshing) return refreshRequest
    isRefreshing = true

    refreshRequest = authApi.refreshToken(authState.current.refreshToken).finally(() => (isRefreshing = false))
    return refreshRequest
  }

  http.interceptors.request.use((config) => {
    return ensureAuthorization().then(({ accessToken, accessExpires }) => {
      authState.current.accessToken = accessToken
      authState.current.accessExpires = accessExpires

      config.headers.Authorization = `Bearer ${accessToken}`
      return config
    })
  })

  http.interceptors.response.use(
    (response) => response,
    (err) => {
      const shouldLogout = err.response && err.response.status === 401

      if (shouldLogout) {
        setAuthState({ accessExpires: 0, accessToken: '', refreshToken: '', refreshExpires: 0 })
      }

      throw err
    }
  )
}

export const handleHttpResponse = <T extends any>(response: AxiosResponse<T>): IHTTPSuccessResponse<T> => {
  return { status: 'success', body: response.data }
}

export const handleHttpError = (error: AxiosError): IHTTPErrorResponse => {
  if (error?.response?.status === 429) return { status: 'error', message: 'Слишком много запросов..' }

  if (error?.response?.status === 400) {
    const validationError = error?.response?.data as Record<string, string[] | string>
    if (validationError) {
      for (const key in validationError) {
        if (Array.isArray(validationError[key])) validationError[key] = validationError[key][0]
      }
    }

    return {
      status: 'error',
      message: 'С вашими данными что-то не так 🤔',
      body: error?.response?.data as Record<string, string>,
    }
  }

  return { status: 'error', message: error?.message }
}

export default http
